if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.buyNowButton = this.querySelector("button[data-buy-now]");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        if (this.buyNowButton)
          this.buyNowButton.addEventListener(
            "click",
            this.onBuyNowHandler.bind(this),
          );
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      getComplementaryProductsData() {
        const productInfo =
          this.closest("product-info") || document.querySelector("product-info");
        if (!productInfo) return [];
        const product_inputs = productInfo.querySelectorAll(
          "input[name^='complementary_products']:checked, [data-cart-addon-input]:checked",
        );
        if (!product_inputs) return [];
        const itemQuantities = new Map();
        product_inputs.forEach((item) => {
          const id = Number(item.value);
          const quantity = Math.max(1, Number(item.dataset.quantity || 1));
          if (!id) return;
          itemQuantities.set(id, (itemQuantities.get(id) || 0) + quantity);
        });
        return Array.from(itemQuantities, ([id, quantity]) => ({ id, quantity }));
      }

      onBuyNowHandler(evt) {
        evt.preventDefault();
        if (this.getPricing()) {
          this.onSubmitHandler(evt, true);
          return;
        }
        const items = this.getComplementaryProductsData();
        const items_params = items.reduce((acc, item) => {
          acc += `,${item.id}:${item.quantity}`;
          return acc;
        }, "");
        const formData = new FormData(this.form);
        const data = Object.fromEntries(formData);
        window.location.href = `${window.routes.cart_url}/${data.id}:${data.quantity}${items_params}?checkout`;
      }

      getPricing() {
        const root = this.closest('product-info');
        if (root) return root.querySelector('product-discount-offer')?.pricing;
        const sticky = this.closest('product-sticky-bar');
        return sticky ? document.querySelector(`product-info[data-product-id="${sticky.dataset.productId}"] product-discount-offer`)?.pricing : null;
      }

      onSubmitHandler(evt, checkout = false) {
        evt.preventDefault();
        const items = this.getComplementaryProductsData();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        config.headers["Content-Type"] = "application/json";

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        let bodyData = {
          sections: formData.get("sections"),
        };
        
        if (items.length > 0) {
          bodyData.items = [
            { id: formData.get("id"), quantity: formData.get("quantity") },
            ...items
          ];
        } else {
          bodyData.id = formData.get("id");
          bodyData.quantity = formData.get("quantity");
        }

        config.body = JSON.stringify(bodyData);

        const pricing = this.getPricing();
        const request = pricing
          ? pricing.addToCart(this.cart?.getSectionsToRender().map((section) => section.id) || [])
          : fetch(`${routes.cart_add_url}`, config).then((response) => response.json());

        return request
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            }
            if (response.warning) this.handleErrorMessage(response.warning);
            if (checkout && !response.warning) {
              window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
              return;
            }
            if (!this.cart || (pricing && this.cart.getSectionsToRender().some((section) => !response.sections?.[section.id]))) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker(
              "add:wait-for-subscribers",
            );
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  "add:wait-for-subscribers",
                  startMarker,
                );
              });
            this.error = false;
            this.submitButton.classList.add("is-success");
            this.submitButtonText.textContent = "✓ Added";

            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      "add:paint-updated-sections",
                      () => {
                        this.cart.renderContents(response);
                      },
                    );
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            this.handleErrorMessage(e.message || window.cartStrings.error);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) {
              this.submitButton.removeAttribute("aria-disabled");
              setTimeout(() => {
                this.submitButton.classList.remove("is-success");
                this.submitButtonText.textContent = window.variantStrings.addToCart;
              }, 1500);
            }
            this.querySelector(".loading__spinner").classList.add("hidden");

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    },
  );
}
